/**
 * Workflow Studio — Unified tabbed hub for all workflow tooling.
 *
 * DRR-126 Amendment (2026-03-24): Constellation living view is the PRIMARY tab.
 * Composition is emergent from constellation membership — the constellation IS
 * the master workflow view. Tabulator CRUD list remains accessible but is not
 * the entry point.
 *
 * Tab layout (DRR-126 amendment):
 *   1. Living View — constellation + live data (default landing)
 *   2. Composer — AI-assisted workflow editor (DRR-168 Agent SDK)
 *   3. Fragment Inspector — DAGCanvas drill-down from constellation nodes
 *   4. Workflows — list/CRUD via TabulatorPrimitiveManager
 *   5. Documents — Chaos-to-DSL pipeline (DocumentStudioPage content)
 *   6. PDF Tools — annotation, signing, comparison
 *   7. System — dashboard with health, visualizer, lifecycle plane
 *
 * Heavy tabs are lazy-loaded to keep initial bundle small.
 * Supports ?tab= query param for deep-linking (e.g. ?tab=composer).
 *
 * FERP Principles:
 * - Mantine v8.x only (NO MUI)
 * - Dumb Frontend: each tab delegates to existing components
 * - Repository pattern: data via generated GraphQL hooks
 * - Constellation = coordination fabric, not org chart (DRR-126)
 */

import React, { Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Drawer,
  Tabs,
  Title,
  Group,
  Badge,
  Loader,
  Center,
  Stack,
  Text,
} from '@mantine/core';
import {
  IconList,
  IconFileText,
  IconPdf,
  IconTopologyStarRing3,
  IconDashboard,
  IconWand,
  IconZoomCode,
} from '@tabler/icons-react';
import { useTenantId } from '@/auth';

// ── Eager: lightweight list page ──────────────────────────────────────
import { TabulatorPrimitiveManager } from '@/components/primitives/TabulatorPrimitiveManager';

// ── Lazy: heavy pages loaded on demand ────────────────────────────────
const LazyConstellation = React.lazy(
  () => import('../constellation').then((m) => ({ default: m.ConstellationPage })),
);
const LazyLlmEditor = React.lazy(
  () => import('./LlmWorkflowEditorPage'),
);
const LazyFragmentComposer = React.lazy(
  () => import('./FragmentComposerPage'),
);
const LazyUnifiedDesigner = React.lazy(
  () => import('@ferp/workflow-forms').then((m) => ({ default: m.UnifiedWorkflowDesigner })),
);
const LazyDocumentStudio = React.lazy(
  () => import('../document-studio/DocumentStudioPage').then((m) => ({ default: m.DocumentStudioPage })),
);
const LazyPdfStudio = React.lazy(
  () => import('../pdf-studio/PdfStudioPage').then((m) => ({ default: m.PdfStudioPage })),
);
const LazySystemIntegrator = React.lazy(
  () => import('@ferp/workflow-forms').then((m) => ({ default: m.WorkflowSystemIntegrator })),
);

// ── Tab definitions ───────────────────────────────────────────────────

// DRR-126 amendment: constellation living view is the default landing tab.
// Composition is emergent from constellation membership — this IS the master workflow.
const TABS = [
  { value: 'living', label: 'Living View', icon: IconTopologyStarRing3 },
  { value: 'composer', label: 'Composer', icon: IconWand },
  { value: 'inspector', label: 'Fragment Inspector', icon: IconZoomCode },
  { value: 'workflows', label: 'Workflows', icon: IconList },
  { value: 'documents', label: 'Documents', icon: IconFileText },
  { value: 'pdf', label: 'PDF Tools', icon: IconPdf },
  { value: 'system', label: 'System', icon: IconDashboard },
] as const;

type TabValue = (typeof TABS)[number]['value'];

// ── Loading fallback ──────────────────────────────────────────────────

function TabLoader() {
  return (
    <Center py="xl">
      <Loader size="lg" />
    </Center>
  );
}

// ── Page ──────────────────────────────────────────────────────────────

const VALID_TABS = new Set<string>(TABS.map((t) => t.value));

export function WorkflowStudioPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const activeTab: TabValue = rawTab && VALID_TABS.has(rawTab) ? (rawTab as TabValue) : 'living';
  const initialWorkflowId = searchParams.get('workflowId') ?? undefined;
  const initialWorkflowName = searchParams.get('workflowName') ?? undefined;
  const tenantId = useTenantId() ?? '';

  const handleTabChange = (value: string | null) => {
    const tab = (value as TabValue) ?? 'living';
    setSearchParams(tab === 'living' ? {} : { tab }, { replace: true });
  };

  return (
    <Container fluid p="md">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group gap="sm">
            <Title order={3}>Workflow Studio</Title>
            <Badge variant="light" color="violet" size="sm">
              7 tools
            </Badge>
          </Group>
          <Text size="xs" c="dimmed">
            Unified workflow design, orchestration, and document processing
          </Text>
        </Group>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          keepMounted={false}
        >
          <Tabs.List>
            {TABS.map((tab) => (
              <Tabs.Tab
                key={tab.value}
                value={tab.value}
                leftSection={<tab.icon size={16} />}
              >
                {tab.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>

          {/* ── Living View (DRR-126: constellation = master workflow) ── */}
          <Tabs.Panel value="living" pt="md">
            <Suspense fallback={<TabLoader />}>
              <LazyConstellation />
            </Suspense>
          </Tabs.Panel>

          {/* ── Composer (DRR-171: Fragment composition studio) ── */}
          <Tabs.Panel value="composer" pt="md">
            <Suspense fallback={<TabLoader />}>
              <LazyFragmentComposer />
            </Suspense>
          </Tabs.Panel>

          {/* ── Fragment Inspector (DAGCanvas drill-down from constellation) ── */}
          <Tabs.Panel value="inspector" pt="md">
            <Suspense fallback={<TabLoader />}>
              <LazyUnifiedDesigner
                tenant={tenantId}
                initialWorkflowId={initialWorkflowId}
                initialWorkflowName={initialWorkflowName}
                renderWorkflowBrowser={({ opened, onClose, onSelectWorkflow }) => (
                  <Drawer
                    opened={opened}
                    onClose={onClose}
                    position="right"
                    size="80%"
                    title="Workflow Browser"
                    styles={{ title: { fontWeight: 700, fontSize: '1.25rem' } }}
                  >
                    <TabulatorPrimitiveManager
                      primitiveType="workflow"
                      compact
                      onSelect={(item) => onSelectWorkflow(item)}
                    />
                  </Drawer>
                )}
              />
            </Suspense>
          </Tabs.Panel>

          {/* ── Workflows (primitive CRUD) ──────────────── */}
          <Tabs.Panel value="workflows" pt="md">
            <TabulatorPrimitiveManager primitiveType="workflow" />
          </Tabs.Panel>

          {/* ── Documents (Chaos-to-DSL) ──────────────────── */}
          <Tabs.Panel value="documents" pt="md">
            <Suspense fallback={<TabLoader />}>
              <LazyDocumentStudio />
            </Suspense>
          </Tabs.Panel>

          {/* ── PDF Tools ─────────────────────────────────── */}
          <Tabs.Panel value="pdf" pt="md">
            <Suspense fallback={<TabLoader />}>
              <LazyPdfStudio />
            </Suspense>
          </Tabs.Panel>

          {/* ── System (health, visualizer, lifecycle) ──── */}
          <Tabs.Panel value="system" pt="md">
            <Suspense fallback={<TabLoader />}>
              <LazySystemIntegrator />
            </Suspense>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}

export default WorkflowStudioPage;
